import React, { useContext, useState, useEffect, useRef} from 'react';
import { ICONS } from '../../../general-components/icon';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import SelectSmall from '../../../general-components/select-small';
import AnimationRangeInput from './animation-range-input';
import DurationInput from '../../../general-components/duration-input';
import { animationTiming, animateInOptions} from './types';
import AnimationSectionHeader from './animation-section-header';
import { AnimationsContext } from './animations-context';
import { DURATION_INPUT_TYPES } from '../../../../types/constants';
import TooltipWrapper from '../../../general-components/tooltips/tooltip-wrapper';

const maxValue = 24;
const minValue = 6;

export default function DurationSectionAnimations({isStylepack = false}) {
	const formContext = useContext(FormContext);
	const { statefulFormValues, onChange } = formContext || {};

	const {
		handleAnimationInChange,
		handleShowForDurationChange,
		selectedInTransition,
		delayInDuration,
		setDelayInDuration,
		showFor
	} = useContext(AnimationsContext);
	
	const [localDuration, setLocalDuration] = useState(maxValue);
	const [dropdownOption, setDropdownOption] = useState(selectedInTransition);
	const [showMinValueTooltip, setShowMinValueTooltip] = useState(false);
	const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const internalUpdateRef = useRef(false);

	useEffect(() => {
		// skip sync when this component just wrote to the form
		if (internalUpdateRef.current) {
			internalUpdateRef.current = false;
			return;
		}
		const formVal = Number(statefulFormValues.seconds);
		if (!isNaN(formVal)) {
			// stylepack item, use form value
			setLocalDuration(Math.max(formVal, minValue))
		} else if (showFor > 0) {
			// else use basic overlay animations
			setLocalDuration(Math.max(showFor, minValue))
		} else {
			// never been set
			if (isStylepack) {
				//stylepack items default to animate out after 6s
				setLocalDuration(minValue);
			} else {
				// by default, items show for forever
				setLocalDuration(maxValue);
			}
		}
	}, [statefulFormValues?.seconds, showFor, isStylepack]);

	useEffect(() => {
		const initialValue = animateInOptions.find((op) => op.value === selectedInTransition.value);
		if (initialValue) setDropdownOption(initialValue);
	}, [selectedInTransition])
	
	// Debounced callback to update form after 500ms of no changes
	const formUpdate = (value: number) => {
		const isInf = value >= maxValue;
		//@ts-expect-error as ChangeEvent<HTMLInputElement>
		onChange({target: {name: 'seconds', value: isInf ? 8 : value}});
		//@ts-expect-error as ChangeEvent<HTMLInputElement>
		onChange({target: {name: 'animate-out', value: !isInf}});
	};
	
	const handleSliderChange = (value: number) => {
		setLocalDuration(value); // Immediate UI update
		internalUpdateRef.current = true;

		// Use callback if provided, otherwise use form context
		if (isStylepack) {
			formUpdate(value);
		} else {
			handleShowForDurationChange(value >= maxValue ? -1 : value);
		}
	};

	const handleAnimationStartChoice = (choice: animationTiming) => {
		const option = animateInOptions.find((op) => op.value === choice) ?? animateInOptions[0];
		setDropdownOption(option);
		handleAnimationInChange(option.value);
	}

	const handleClampedToMin = () => {
		// Clear any existing timeout
		if (tooltipTimeoutRef.current) {
			clearTimeout(tooltipTimeoutRef.current);
		}

		// Show tooltip
		setShowMinValueTooltip(true);

		// Hide tooltip after 5 seconds
		tooltipTimeoutRef.current = setTimeout(() => {
			setShowMinValueTooltip(false);
		}, 5000);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (tooltipTimeoutRef.current) {
				clearTimeout(tooltipTimeoutRef.current);
			}
		};
	}, []);

	// Generate exactly 4 ticks with round numbers, last tick at 24 labeled as ∞
	// minValue is going to be 6 for all for now but we have the minValue in currTemplate.data
	const generateTicks = (): Array<{
		label: string;
		position?: 'left' | 'center' | 'right';
		isInfinity?: boolean;
	}> => {
		const ticks: {
			label: string;
			position?: 'left' | 'center' | 'right';
			isInfinity?: boolean;
		}[] = [];
		const numTicks = 4;
		const interval = (maxValue - minValue) / (numTicks - 1);

		for (let i = 0; i < numTicks; i++) {
			if (i === numTicks - 1) {
				// Last tick is infinity at position 24
				ticks.push({ label: '∞', position: 'right', isInfinity: true });
			} else {
				const value = Math.round(minValue + (i * interval));
				const position: 'left' | 'center' | 'right' = value < 8 ? 'left' : value > 8 ? 'right' : 'center';
				ticks.push({ label: `${value}s`, position });
			}
		}

		return ticks;
	};

	return (<>
			<div style={{marginTop: '16px'}}/>
			<div className='flex-center'>

				<AnimationSectionHeader
					text='Show for'
					icon={ICONS.ANIMATION_TIMER}
				/>
				<TooltipWrapper message={`${minValue}s is the min duration for this asset`} forceShow={showMinValueTooltip} disabled={!showMinValueTooltip}>
					<DurationInput
						inputType={DURATION_INPUT_TYPES.SECONDS}
						className='stylepack-animation-input-container'
						onBlur={(value: number) => handleSliderChange(value)}
						value={localDuration}
						minValue={`${minValue}`}
						maxValue={`${maxValue}`}
						iconOnMax='∞'
						onChangeDebounce
						onClampedToMin={handleClampedToMin}
					/>
				</TooltipWrapper>
			</div>
			<AnimationRangeInput
				value={localDuration}
				onChange={handleSliderChange}
				min={minValue}
				max={maxValue}
				step={0.5}
				ticks={generateTicks()}
			/>
			<div className='flex-center justify-space-between margin-top-medium'>
				<SelectSmall
					options={animateInOptions}
					label={dropdownOption.label}
					selected={dropdownOption}
					onChange={(value) => handleAnimationStartChoice(value)}
					hasCarrot={true}
					className='animation-select-dropdown'
				/>
				{dropdownOption?.value === animationTiming.delayed ? (
					<DurationInput
						inputType={DURATION_INPUT_TYPES.SECONDS}
						value={delayInDuration}
						onBlur={(value: number) => setDelayInDuration(value)}
						minValue={'0'}
					/>
				) : null}
			</div>
			
	</>);
}